import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { getUserToken } from '@/redux/user';
import { computeText } from '@/locales/utils';
import { Label } from '@/components/atoms/Label';
import {
    useGetLocationAutocompleteQuery,
    useGetLocationPlaceDetailsQuery,
} from '@/services/innovorder/pickupPoint/pickupPoint.endpoints';
import type { FormattedAddress } from '@/services/innovorder/pickupPoint/pickupPoint.type';

type AddressAutocompleteProps = {
    value: string;
    onChange: (address: FormattedAddress) => void;
    required?: boolean;
    labelId?: string;
};

const Wrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
`;

const StyledInput = styled.input`
    width: 100%;
    box-sizing: border-box;
    min-height: 40px;
    padding: 10px 16px;
    background: ${({ theme }) => theme.color.white200};
    border: 1px solid ${({ theme }) => theme.color.grey300};
    border-radius: ${({ theme }) => theme.radius.main};
    font-size: ${({ theme }) => theme.font.size.normal};
    color: ${({ theme }) => theme.color.black200};
    font-weight: 600;
    outline: none;

    &:focus {
        border-color: ${({ theme }) => theme.color.black300};
    }

    &::placeholder {
        color: ${({ theme }) => theme.color.black300};
        font-size: ${({ theme }) => theme.font.size.small};
    }
`;

const Dropdown = styled.ul`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 10;
    margin: 0;
    padding: 0;
    list-style: none;
    background: ${({ theme }) => theme.color.white100};
    border: 1px solid ${({ theme }) => theme.color.grey200};
    border-radius: ${({ theme }) => theme.radius.main};
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    max-height: 200px;
    overflow-y: auto;
`;

const DropdownItem = styled.li`
    padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
    cursor: pointer;
    font-size: ${({ theme }) => theme.font.size.normal};
    color: ${({ theme }) => theme.color.black400};

    &:hover {
        background: ${({ theme }) => theme.color.grey100};
    }
`;

export const AddressAutocomplete: React.FunctionComponent<AddressAutocompleteProps> = ({
    value,
    onChange,
    required,
    labelId = 'restaurantInfo.address',
}) => {
    const intl = useIntl();
    const token = useSelector(getUserToken);
    const [inputValue, setInputValue] = useState(value);
    const [searchText, setSearchText] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
    const [sessionToken] = useState(() => crypto.randomUUID());
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    const { data: locationData } = useGetLocationAutocompleteQuery(
        { searchText, sessiontoken: sessionToken, token: token || '' },
        { skip: searchText.length <= 2 || !token },
    );

    const { data: placeData } = useGetLocationPlaceDetailsQuery(
        { placeId: selectedProviderId || '', sessiontoken: sessionToken, token: token || '' },
        { skip: !selectedProviderId || !token },
    );

    useEffect(() => {
        if (placeData?.place && selectedProviderId) {
            onChange(placeData.place);
        }
    }, [placeData?.place, selectedProviderId]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setInputValue(val);
        setSearchText(val);
        setIsOpen(val.length > 2);
    }, []);

    const handleSelect = useCallback((suggestion: { name: string; providerId: string }) => {
        setInputValue(suggestion.name);
        setSearchText('');
        setIsOpen(false);
        setSelectedProviderId(suggestion.providerId);
    }, []);

    const suggestions = locationData?.suggestions ?? [];

    return (
        <Wrapper ref={wrapperRef}>
            <Label labelId={labelId} required={required} />
            <StyledInput
                value={inputValue}
                onChange={handleInputChange}
                placeholder={computeText(intl, 'restaurantInfo.address')}
                onFocus={() => {
                    if (searchText.length > 2) setIsOpen(true);
                }}
            />
            {isOpen && suggestions.length > 0 && (
                <Dropdown>
                    {suggestions.map((s) => (
                        <DropdownItem key={s.providerId} onClick={() => handleSelect(s)}>
                            {s.name}
                        </DropdownItem>
                    ))}
                </Dropdown>
            )}
        </Wrapper>
    );
};
